import React, { useMemo } from "react";

import { Box } from "@mui/system";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { ArrowRightAlt } from "@mui/icons-material";

const isValidMap = () => {
  return true;
};

const RAINBOW = [
  { startColor: "#F00", endColor: "#ff9a00" },
  { startColor: "#ff9a00", endColor: "#d0de21" },
  { startColor: "#d0de21", endColor: "#4fdc4a" },
  { startColor: "#4fdc4a", endColor: "#2fc9e2" },
  { startColor: "#2fc9e2", endColor: "#1c7fee" },
  { startColor: "#1c7fee", endColor: "#5f15f2" },
  { startColor: "#5f15f2", endColor: "#ba0cf8" },
  { startColor: "#ba0cf8", endColor: "#fb07d9" },
  { startColor: "#fb07d9", endColor: "#F00" },
];

// const HILL_SHADE = { startColor: '#000', endColor: '#FFF' };

export const formatExpression = (fill) => {
  let bareExpression = fill?.expressionObject
    ? fill.expressionObject?.expression
    : fill.expression;

  if (fill.expressionObject) {
    for (let i = 0; i < fill.expressionObject.properties.length; i++) {
      bareExpression = bareExpression.replaceAll(
        `property${i + 1}`,
        fill.expressionObject.properties[i],
      );
    }

    for (let i = 0; i < fill.expressionObject.values.length; i++) {
      bareExpression = bareExpression.replaceAll(
        `value${i + 1}`,
        fill.expressionObject.values[i],
      );
    }
  }
  return bareExpression;
};

export const useBlocks = (props) => {
  const { bands, style } = { ...props };
  const memoedBlocks = useMemo(() => {
    const blocks = [];
    var tooltip = null;

    if (
      style?.parameters.fill.type === "expression" ||
      style?.parameters.fill.type === "channelMap"
    ) {
      tooltip =
        style?.parameters.fill.type === "expression" &&
        "Uses the color property of the feature";
    } else if (style?.parameters.fill.type === "seededRandom") {
      tooltip = `Assigns a random color based on ${formatExpression(style?.parameters.fill)}`;
      blocks.push(...RAINBOW);
    } else if (style?.parameters.fill.type === "constant") {
      tooltip = "Assigns a single color to all features";
      blocks.push({
        startColor: style?.parameters?.fill.target.color,
        endColor: style?.parameters?.fill.target.color,
        pattern: style?.parameters?.fill.target.pattern,
        type: style?.parameters?.fill.target.type,
        icon: style?.parameters?.fill.target.icon,
      });
    } else if (style?.parameters.fill.type === "caseMap") {
      blocks.push(
        ...(style?.parameters?.fill.caseMap?.map((rule) => ({
          startColor: rule?.target.color,
          endColor: rule?.target.color,
          pattern: rule?.target.pattern,
          type: rule?.target.type,
          icon: rule?.target.icon,
          label: rule.label ?? `${formatExpression(rule)}`,
        })) ?? []),
      );
    } else if (style?.parameters.fill.type === "rangeMap") {
      const title = "Hoogte";
      blocks.push(
        {
          startColor: style.parameters.fill.rangeMap[0]?.target.color,
          endColor: style.parameters.fill.rangeMap[0]?.target.color,
          pattern: style.parameters.fill.rangeMap[0]?.target.pattern,
          icon: style.parameters.fill.rangeMap[0]?.target.icon,
          type: style.parameters.fill.rangeMap[0]?.target.type,
          label:
            style?.parameters.fill.rangeMap[0]?.label ??
            `${title}: lager <> ${Math.round(style?.parameters.fill.rangeMap[0]?.value * 100) / 100}m`,
        },
        ...(style?.parameters?.fill.rangeMap?.map((rule, i) => {
          let label;

          label =
            rule.label ??
            `${title}: ${Math.round(rule?.value * 100) / 100}m <> ${
              style?.parameters?.fill.rangeMap[i + 1]?.value
                ? `${
                    Math.round(
                      style?.parameters?.fill.rangeMap[i + 1]?.value * 100,
                    ) / 100
                  }m`
                : "hoger"
            }`;

          return {
            startColor: rule?.target.color,
            endColor:
              (style?.parameters?.fill.gradient
                ? style?.parameters?.fill.rangeMap[i + 1]?.target.color
                : undefined) ?? rule?.target.color,
            pattern: rule?.target.pattern,
            icon: rule?.target.icon,
            type: rule?.target.type,
            label: label,
          };
        }) ?? []),
      );
    } else if (style?.parameters.fill.type === "valueMap") {
      blocks.push(
        ...(style?.parameters?.fill.valueMap?.map((rule) => {
          return {
            startColor: rule?.target.color,
            endColor: rule?.target.color,
            pattern: rule?.target.pattern,
            type: rule?.target.type,
            icon: rule?.target.icon,
            label: `Hoogte: ${rule?.value}m`,
          };
        }) ?? []),
      );
    }

    const filteredBlocks = blocks?.filter((b) => !!b?.label);

    return { blocks, tooltip, filteredBlocks };
  }, [style?.parameters, bands]);

  return memoedBlocks;
};

const StylingVisualisation = ({ type, layer, style }) => {
  console.log("layerx", layer);

  if (!layer) {
    return null;
  }

  const bands = layer.bands;
  return (
    <Visualisation
      defaultColor={style?.parameters.fill.defaultTarget?.color}
      type={type}
      bands={bands}
      style={style}
    />
  );
};

const Legend = ({ type, style }) => {
  const bands = [
    { name: "hoogte", number: 1 },
    { name: "transparant", number: 2 },
  ];
  return (
    <Box>
      {type === "presentationMode" && (
        <Typography variant="h2" gutterBottom>
          Legenda
        </Typography>
      )}
      <Visualisation
        defaultColor={
          (style?.method === "transitionPoints" || style?.method === "rules") &&
          style.parameters.defaultColor
        }
        type={type}
        bands={bands}
        style={style}
      />
    </Box>
  );
};

export default Legend;

const useStyles = makeStyles({ name: "Visualisation" })(
  (theme, { isPresentationMode, height }) => ({
    root: {
      display: "flex",
      gap: theme.spacing(1),
      height,
      maxHeight: isPresentationMode ? "33vh" : undefined,
      overflow: isPresentationMode ? "auto" : undefined,
      overscrollBehavior: isPresentationMode ? "auto" : undefined,
    },
    defaultColor: {
      width: height,
      flexShrink: 0,
      borderRadius: height,
      overflow: "hidden",
    },
    lineItem: {
      display: "flex",
      flexWrap: "nowrap",
      flexGrow: 1,
      height: "100%",
      borderRadius: height,
      overflow: "hidden",
      "&>*": { flex: "1 1 0px" },
    },
    block: {
      height: "100%",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      "& svg": { transform: "rotate(-45deg)", height, width: height },
      //background: 'white',
    },
  }),
);

const Visualisation = ({ type, defaultColor, style, bands }) => {
  const { blocks, tooltip, filteredBlocks } = useBlocks({ bands, style });
  const hasIcon = new Set((blocks ?? [])?.map((block) => !!block?.icon)).has(
    true,
  );

  const { classes: styles } = useStyles({
    isPresentationMode: type === "presentationMode",
    height: type === "presentationMode" ? "auto" : hasIcon ? 24 : 8,
  });

  const preppedBlock = useMemo(
    () =>
      blocks.map((block, i) => {
        if (block) {
          const sx = {
            opacity:
              typeof style?.parameters?.alpha === "number"
                ? (style?.parameters?.alpha + 0.25) / 1.25
                : 1,
            overflow: "hidden",
            ...(!block?.arrow
              ? {
                  background:
                    block?.icon || block?.pattern
                      ? undefined
                      : block?.img
                        ? `url(${block.img})`
                        : `linear-gradient(90deg, ${block?.startColor} 0%, ${block?.endColor} 100%)`,
                }
              : {}),
            ...(type === "presentationMode"
              ? {
                  maxHeight: 24,
                  width: 24,
                  borderRadius: 1,
                  height: 24,
                }
              : {}),
          };

          const inner = (
            <Box key={"Box_" + i} className={styles.block} sx={sx}>
              {block?.arrow && <ArrowRightAlt color="error" />}
            </Box>
          );

          return type === "presentationMode" && filteredBlocks?.length > 0 ? (
            <ListItem sx={{ alignItems: "stretch" }} key={"ListItem" + i}>
              <ListItemIcon sx={{ alignItems: "center" }}>{inner}</ListItemIcon>
              {!!block?.label && (
                <ListItemText
                  primary={block?.label}
                  sx={{ overflowWrap: "anywhere" }}
                />
              )}
            </ListItem>
          ) : block?.label ? (
            <Tooltip
              key={"Tooltip_" + i}
              title={block?.label}
              slots={{ transition: Zoom }}
            >
              <span>{inner}</span>
            </Tooltip>
          ) : (
            inner
          );
        }

        return null;
      }),
    [
      blocks,
      filteredBlocks?.length,
      style?.parameters?.alpha,
      styles.block,
      type,
    ],
  );

  const inner =
    type === "presentationMode" ? (
      <List disablePadding dense>
        {filteredBlocks?.length > 0 ? (
          preppedBlock
        ) : (
          <ListItem sx={{ alignItems: "stretch" }} key="listItem-noBlocks">
            <ListItemIcon sx={{ alignItems: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  height: 24,
                  width: 24,
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                {preppedBlock}
              </Box>
            </ListItemIcon>
            <ListItemText primary={tooltip} sx={{ overflowWrap: "anywhere" }} />
          </ListItem>
        )}
      </List>
    ) : (
      <div className={styles.lineItem}>{preppedBlock}</div>
    );

  return (
    <div className={styles.root}>
      {!!tooltip && type !== "presentationMode" ? (
        <Tooltip title={tooltip} slots={{ transition: Zoom }}>
          {inner}
        </Tooltip>
      ) : (
        inner
      )}
      {defaultColor && (
        <Tooltip title="Default Fill" slots={{ transition: Zoom }}>
          <Box
            className={styles.defaultColor}
            sx={{ background: defaultColor }}
          />
        </Tooltip>
      )}
    </div>
  );
};

const useListItemTextStyles = makeStyles({
  name: "StylingListItemText",
})((theme, _params, classes) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(1),
    [`&.${classes.disabled}`]: { opacity: 0.5, lineHeight: "100%" },
    margin: 0,
  },
  primary: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [`& .${classes.name}`]: {
      textTransform: "initial",
      lineHeight: "100%",
      overflow: "clip",
      overflowClipMargin: 4,
      minWidth: 150,
    },
    [`& .${classes.disabled}`]: { flexGrow: 1 },
  },
  name: {},
  primaryText: {},
  disabled: {},
  changed: { lineHeight: "100%" },
  style: { marginBottom: theme.spacing(0.5) },
}));

const defaultProps = {};

export const StylingListItemText = ({
  type = "styling",
  style,
  disabled = false,
  hideName = false,
  layer,
  asList = true,
  primaryTypographyProps = defaultProps,
  secondaryTypographyProps = defaultProps,
}) => {
  const { classes: styles, cx } = useListItemTextStyles();
  const { className: primaryTypographyClassName, propsPrimaryTypography } = {
    ...primaryTypographyProps,
  };
  // const { className: secondaryTypographyClassName, propsSecondaryTypography } = { ...secondaryTypographyProps };
  console.log("here");
  if (!layer) return null;

  const name =
    type === "legend" && isValidMap(layer).available
      ? layer.name
      : type === "legend"
        ? layer.name + " (" + isValidMap(layer).reason + ")"
        : style.name;

  const primary =
    type === "legend" || !hideName || (disabled && !hideName) ? (
      <div className={styles.primary}>
        {!hideName && (
          <Typography
            className={cx(styles.name, primaryTypographyClassName)}
            variant="button"
            noWrap
            {...propsPrimaryTypography}
          >
            {name}
          </Typography>
        )}
        {disabled && !hideName && (
          <Typography
            className={cx(styles.disabled, primaryTypographyClassName)}
            variant="caption"
            align="right"
            {...propsPrimaryTypography}
          >
            (Deselected)
          </Typography>
        )}
      </div>
    ) : undefined;
  console.log("LAYER", layer);
  console.log("XXXXXXXXXXXX");
  const secondary = style ? (
    <StylingVisualisation style={style} layer={layer} type={"styling"} />
  ) : layer.attribution ? (
    <Typography
      variant="body2"
      color="text.secondary"
      {...secondaryTypographyProps}
    >
      {layer.attribution}
    </Typography>
  ) : undefined;

  return asList ? (
    <ListItemText
      className={cx(
        styles.root,
        disabled && styles.disabled,
        style && styles.style,
      )}
      disableTypography
      primary={primary}
      secondary={secondary}
    />
  ) : (
    <>
      {primary}
      {secondary}
    </>
  );
};
